import type { ReactNode } from 'react';
import styled from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconClock } from '#src/atoms/icons/system/iconClock.ts';
import { iconHourglass } from '#src/atoms/icons/system/iconHourglass.ts';
import { BrandXS } from '#src/atoms/typography/index.tsx';
import {
  CornerBadge,
  type Position,
} from '#src/components/CornerBadge/index.tsx';

const COLOR_MAP = {
  purple: 'linear-gradient(228.5deg, #AD00FF -13.01%, #680099 62%)',
  gold: 'linear-gradient(188deg, rgb(255, 255, 255) 1.37%, rgb(252, 255, 99) 36.66%, rgb(194, 105, 0) 101.57%)',
  red: 'var(--c-nl-500)',
} as const;

const ICON_MAP = {
  date: iconHourglass,
  countdown: iconClock,
} as const;

const Root = styled.div`
  display: inline-flex;
  &.red > * {
    color: var(--c-rewards-orange);
    box-shadow: 0 0 12px rgba(var(--c-rgb-rewards-orange), 0.45);
  }
`;
const Badge = styled.div`
  display: inline-flex;
  gap: var(--half-unit);
  padding: 2px var(--half-unit);
  border-radius: var(--half-unit);
  color: var(--c-black);
  .purple & {
    color: var(--c-white);
  }
`;

type TimeLabelState = 'date' | 'countdown';
type TimeLabelType = 'banner' | 'badge';

type Props = {
  children: ReactNode;
  state: TimeLabelState;
  type?: TimeLabelType;
  color?: 'purple' | 'gold' | 'red';
  align?: Position;
};

export const TimeLabel = ({
  children,
  state,
  type = 'badge',
  color = 'gold',
  align,
}: Props) => {
  const content = (
    <>
      <FontAwesomeIcon icon={ICON_MAP[state]} />
      <BrandXS>{children}</BrandXS>
    </>
  );

  if (type === 'banner') {
    return (
      <Root className={color}>
        <CornerBadge color={COLOR_MAP[color]} align={align}>
          {content}
        </CornerBadge>
      </Root>
    );
  }

  return (
    <Root className={color}>
      <Badge style={{ background: COLOR_MAP[color] }}>{content}</Badge>
    </Root>
  );
};
